import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, UserCheck, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field } from "@/components/Forms";
import { DIALOG_CLS, ConfirmDialog } from "@/components/Dialogs";
import { api, apiError } from "@/lib/api";

export function useAccounts() {
  const [list, setList] = useState(null);
  const refresh = useCallback(() => api.get("/users").then((r) => setList(r.data)).catch(() => setList([])), []);
  useEffect(() => { refresh(); }, [refresh]);
  return { list: list || [], loaded: list !== null, refresh };
}

function PasswordForm({ withUser, onSubmit, label }) {
  const [f, setF] = useState({ username: "", password: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (f.password.length < 8) return toast.error("A senha precisa ter pelo menos 8 caracteres.");
    setBusy(true);
    try { await onSubmit(f); } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="account-form">
      {withUser && <Field label="Usuário de acesso"><Input autoCapitalize="none" placeholder="ex.: ana.bella" value={f.username} onChange={(e) => setF({ ...f, username: e.target.value.toLowerCase().replace(/\s/g, "") })} data-testid="account-username-input" /></Field>}
      <Field label={withUser ? "Senha inicial (mín. 8 caracteres)" : "Nova senha (mín. 8 caracteres)"}><Input type="text" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="account-password-input" /></Field>
      <p className="text-xs text-neutral-500">{withUser ? "Use letras minúsculas, números, ponto ou hífen, sem espaços. Passe o usuário e a senha para a pessoa; ela pode trocar a senha depois em \"Alterar senha\"." : "Passe a nova senha para a pessoa; ela pode trocá-la depois em \"Alterar senha\"."}</p>
      <Button type="submit" disabled={busy} className="w-full font-semibold" data-testid="account-save-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : label}</Button>
    </form>
  );
}

export function AccountControl({ account, create, onChange, testId }) {
  const [dlg, setDlg] = useState(null);
  const close = (v) => !v && setDlg(null);
  const done = (msg) => { toast.success(msg); setDlg(null); onChange(); };
  return (
    <>
      {account ? (
        <span className="flex items-center gap-1">
          <span className="hidden max-w-[180px] truncate text-xs text-neutral-500 md:inline" data-testid={`${testId}-username`}>{account.username}</span>
          <Button variant="ghost" size="icon" aria-label="Redefinir senha" onClick={() => setDlg("pw")} data-testid={`${testId}-reset`}><KeyRound className="h-4 w-4 text-gold-dark" /></Button>
          <Button variant="ghost" size="icon" aria-label="Remover acesso" onClick={() => setDlg("del")} data-testid={`${testId}-remove`}><Trash2 className="h-4 w-4 text-neutral-400" /></Button>
        </span>
      ) : (
        <Button size="sm" variant="outline" onClick={() => setDlg("new")} data-testid={`${testId}-create`}><UserCheck className="mr-1 h-4 w-4 text-gold-dark" /> Criar acesso</Button>
      )}
      <Dialog open={dlg === "new" || dlg === "pw"} onOpenChange={close}>
        <DialogContent className={DIALOG_CLS} data-testid={`${testId}-dialog`}>
          <DialogHeader><DialogTitle className="font-display text-xl">{dlg === "new" ? "Criar acesso ao app" : "Redefinir senha"}</DialogTitle></DialogHeader>
          {dlg === "new" && <PasswordForm withUser label="Criar acesso" onSubmit={async (f) => { await create(f); done("Acesso criado."); }} />}
          {dlg === "pw" && <PasswordForm label="Salvar nova senha" onSubmit={async (f) => { await api.put(`/users/${account.id}/password`, { password: f.password }); done("Senha redefinida."); }} />}
        </DialogContent>
      </Dialog>
      <ConfirmDialog open={dlg === "del"} onOpenChange={close} title="Remover acesso?" description={`O usuário ${account?.username || ""} não poderá mais entrar no app.`} testId={`${testId}-remove-dialog`} onConfirm={async () => { try { await api.delete(`/users/${account.id}`); done("Acesso removido."); } catch (e) { toast.error(apiError(e)); } }} />
    </>
  );
}

export function ChangePasswordDialog({ open, onOpenChange }) {
  const [f, setF] = useState({ current: "", password: "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/change-password", f);
      toast.success("Senha alterada.");
      setF({ current: "", password: "" });
      onOpenChange(false);
    } catch (err) { toast.error(apiError(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={DIALOG_CLS} data-testid="change-password-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">Alterar senha</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Senha atual"><Input type="password" value={f.current} onChange={(e) => setF({ ...f, current: e.target.value })} data-testid="current-password-input" /></Field>
          <Field label="Nova senha (mín. 8 caracteres)"><Input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="new-password-input" /></Field>
          <Button type="submit" className="w-full font-semibold" data-testid="change-password-save">Salvar</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
